import { createWithEqualityFn } from "zustand/traditional";
import { ExternalPlaylist } from "@hooks/trpc/useMusicSync";
import { StreamingService } from "../constants";
import type { Journey } from "../analytics/trackOnboarding";

type Genre = {
  id: string;
};

export type ListenerType = "casual" | "music-lover" | "superfan";

interface OnboardingState {
  // State
  journey: Journey | null;
  selectedGenres: Genre[];
  excludedPlaylists: ExternalPlaylist[];
  streamingService: StreamingService | null;
  integrationUserUUID: string;
  quizListenerType: ListenerType | null;
  quizYearsStreaming: number | null;
  hasImportedSongs: boolean;

  // Actions
  setJourney: (journey: Journey) => void;
  setSelectedGenres: (genres: Genre[]) => void;
  setExcludedPlaylists: (
    playlists: ExternalPlaylist[],
    service: StreamingService,
    integrationUserUUID: string,
  ) => void;
  setQuizListenerType: (type: ListenerType) => void;
  setQuizYearsStreaming: (years: number) => void;
  setHasImportedSongs: (value: boolean) => void;
  clearOnboardingData: () => void;
  clearJourney: () => void;
}

export const useOnboardingStore = createWithEqualityFn<OnboardingState>((set) => ({
  // Initial state
  journey: null,
  selectedGenres: [],
  excludedPlaylists: [],
  streamingService: null,
  integrationUserUUID: "",
  quizListenerType: null,
  quizYearsStreaming: null,
  hasImportedSongs: false,

  // Actions
  setJourney: (journey) =>
    set((state) => ({ ...state, journey })),

  setSelectedGenres: (genres) =>
    set((state) => ({ ...state, selectedGenres: genres })),


  setExcludedPlaylists: (playlists, service, integrationUserUUID) =>
    set((state) => ({
      ...state,
      excludedPlaylists: playlists,
      streamingService: service,
      integrationUserUUID,
    })),

  setQuizListenerType: (type) =>
    set((state) => ({ ...state, quizListenerType: type })),

  setQuizYearsStreaming: (years) =>
    set((state) => ({ ...state, quizYearsStreaming: years })),

  setHasImportedSongs: (value) =>
    set((state) => ({ ...state, hasImportedSongs: value })),

  clearOnboardingData: () =>
    set({
      // NOTE: journey is intentionally NOT cleared here.
      // It's needed by the Paywall for analytics after onboarding completes.
      // Paywall clears it via clearJourney() when dismissed or subscribed.
      selectedGenres: [],
          excludedPlaylists: [],
      streamingService: null,
      integrationUserUUID: "",
      quizListenerType: null,
      quizYearsStreaming: null,
      hasImportedSongs: false,
    }),

  clearJourney: () => set({ journey: null }),
}));
